import type {
  Review as ApiReview,
  CreateReviewBody,
  GetReviewsByActivityIdParams,
  Reviews,
  UpdateReviewStatusParams,
  UpdateReviewStatusStatus
} from '@api/models';

import { BrowserApiClient } from '@website/lib/api/browser';
import { getAssetUrl } from '@website/utils/assets';

export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface ReviewItem {
  id: string;
  activityId: string;
  userId: string;
  userName: string;
  userAvatarUrl: string;
  rating: number;
  comment: string;
  tags: string[];
  status: ReviewStatus;
  createdAt: number;
}

export interface ReviewsResult {
  reviews: ReviewItem[];
  cursor: string | null;
  total: number;
}

export interface GetReviewsParams {
  limit?: number;
  cursor?: string;
  status?: ReviewStatus;
}

function mapReview(review: ApiReview): ReviewItem {
  return {
    id: review.id,
    activityId: review.activityId,
    userId: review.userId,
    userName: review.userName,
    userAvatarUrl: getAssetUrl(review.userProfilePictureKey),
    rating: review.rating,
    comment: review.comment,
    tags: (review.tags as string[] | null | undefined) ?? [],
    status: (review.status as ReviewStatus) ?? 'pending',
    createdAt: review.createdAt
  };
}

export const getReviewsByActivityId = async (
  activityId: string,
  params?: Omit<GetReviewsByActivityIdParams, 'status'> & { status?: ReviewStatus }
): Promise<ReviewsResult> => {
  try {
    const response: Reviews = await BrowserApiClient.getReviewsByActivityId(
      activityId,
      params as GetReviewsByActivityIdParams
    );
    return {
      reviews: response.data.map(mapReview),
      cursor: response.cursor ?? null,
      total: response.total
    };
  } catch (error) {
    console.error('Failed to fetch reviews:', error);
    return { reviews: [], cursor: null, total: 0 };
  }
};

export const createReview = async (activityId: string, body: CreateReviewBody): Promise<ReviewItem> => {
  const response = await BrowserApiClient.createReview(activityId, body);
  return mapReview(response);
};

export const updateReviewStatus = async (reviewId: string, status: 'approved' | 'rejected'): Promise<ReviewItem> => {
  const params: UpdateReviewStatusParams = { status: status as UpdateReviewStatusStatus };
  const response = await BrowserApiClient.updateReviewStatus(reviewId, params);
  return mapReview(response);
};
